import { createApi } from '@reduxjs/toolkit/query/react';
import { closeModal } from 'store/modal/modalSlice';
import { setUser } from 'store/user/userSlice';
import { baseQueryWithReauth } from 'utils/services';

import {
  MessageQueryProps,
  TemplateMessage
} from '../patientMessageTemplates/patientMessageTemplates.types';
import type { EditStaffData } from '../staffs/staffs.types';
import type { UserProps } from '../user/user.types';

const tagTypes = [
  'Appointment',
  'Availability',
  'Benefits',
  'QueueScheduleDetails',
  'Billing',
  'Profile',
  'Article',
  'Patient',
  'Task',
  'AgingAlert',
  'AppointmentCategory',
  'AppointmentType',
  'Announcement',
  'BillingConfiguration',
  'Role',
  'Lookup',
  'AppointmentLengths',
  'GlobalAvailability',
  'Staff',
  'Staffs',
  'StaffCost',
  'StaffCosts',
  'PatientChannel',
  'FrontDeskChannel',
  'FrontDeskRequestType',
  'InternalNoteTemplate',
  'PatientMessageTemplate',
  'Notification',
  'Messages',
  'RequestMessageTemplate',
  'TemplateTag',
  'TemplateRequest',
  'SMS',
  'Prescriptions',
  'Trigger',
  'StaffNote',
  'Subscription',
  'Symptom',
  'Reviews',
  'Documents',
  'PatientAppointments',
  'PaymentProfiles',
  'LatestHealthMetrics',
  'HealthMetrics',
  'ChartHealthMetrics',
  'Settings',
  'Event',
  'Tags',
  'PaymentTransactions',
  'Teams',
  'MIFsTypes',
  'MIFs',
  'Invoice',
  'RefundReason',
  'RefundRecommendationHistory',
  'RespondState',
  'PlatformSpecialties',
  'ProgramBundle',
  'MSRTasksStatistics',
  'PerformanceConfig',
  'QueueConfig',
  'QueueRules',
  'ShopSegment',
  'ShopPage',
  'ShopRouting',
  'StoreProducts',
  'Scheduling',
  'MIFConfig',
  'MIFTemplate',
  'MedicationTemplates',
  'MedicationClassPriority',
  'MedicationClassPriorityHistory',
  'ClinicalToolsAuditHistory',
  'Program',
  'Mif',
  'MifConfig',
  'MifSection',
  'MifHistory',
  'MifConfigHistory',
  'MifVersion',
  'MifConfigVersion',
  'EmailTemplate',
  'EmailTemplateVersion',
  'MembershipPlan',
  'LabOrders',
  'LabKitOrders',
  'LabRanges',
  'LabReportResults',
  'LabReports',
  'Workflow',
  'PdmpConfig',
  'PartnerCouponConfig'
];

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getMessage: builder.query<TemplateMessage, MessageQueryProps>({
      query: ({ doctorId, id, patientId, type }) => ({
        params: { doctorId, patientId, type },
        url: `/template-message/${id}/set-values`
      }),
      transformResponse: (response: { data: TemplateMessage }) => response.data
    }),
    getMyProfile: builder.query<UserProps[], void>({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data && data.length) {
          dispatch(setUser({ ...data[0], profileImage: data[0].profileImage || '' }));
          dispatch(closeModal());
        }
      },
      providesTags: ['Profile'],
      query: () => `/my-profile`,
      transformResponse: (response: { data: UserProps[] }) => response.data
    }),

    updateMyProfile: builder.mutation<{ message: string }, { staff: Partial<EditStaffData> }>({
      invalidatesTags: ['Profile'],
      query: ({ staff }) => {
        return {
          body: staff,
          headers: {
            'Content-Type': 'application/json'
          },
          method: 'PATCH',
          url: `/my-profile`
        };
      }
    }),

    updateProfileImage: builder.mutation({
      invalidatesTags: ['Profile', { id: 'LIST', type: 'Staff' }],
      query: ({ profileImage }: { profileImage: File }) => {
        const formData = new FormData();
        formData.append('profileImage', profileImage);

        return {
          body: formData,
          method: 'POST',
          url: `/my-profile/profile-image`
        };
      }
    })
  }),
  reducerPath: 'api',
  tagTypes: tagTypes
});

export const {
  useLazyGetMessageQuery,
  useLazyGetMyProfileQuery,
  useUpdateMyProfileMutation,
  useUpdateProfileImageMutation
} = apiSlice;
